import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Camera, Download, Search, Loader2, ImageOff, ExternalLink, Copy, Check, Archive, User, Calendar, Hash, AlertCircle, RefreshCw, ImagePlus, Images,
} from 'lucide-react';
import {
    buildProjectImageRows,
    filterProjectImageRows,
    describeFileSize,
    resolutionVerdict,
    ProjectImageFilter,
    ProjectImageRow,
    ProjectMedia,
} from '../../services/projectReferenceLogic';
import {
    downloadProjectReferenceImage,
    downloadProjectReferenceImagesZip,
    zipEntriesFor,
    ZipEntry,
} from '../../services/projectReferenceImage';
import { listAllProjectMedia, uploadProjectMedia, MEDIA_TABLE_MISSING_TEXT } from '../../services/projectMediaService';
import type { ListProject } from '../../services/listProject';

interface Props {
    projects: ListProject[];
    onOpenProject?: (project: ListProject) => void;
}

/**
 * Kataloge & Vorlagen → Projektbilder: alle Projekte mit ihren Bildern (Tabelle
 * `project_media`, Nr. 99), Projektname, Auftraggeber, Zeitraum und
 * Beschreibung — Einzel-Download in Originalaufloesung, ZIP je Projekt und
 * fuer alle angezeigten, Upload direkt hier (ohne das Projekt zu oeffnen).
 * Anlass (Betreiber 12.09.2026): Material fuer Social-Media-Beitraege an
 * EINER Stelle einsammeln.
 */
export const AdminProjectImagesView: React.FC<Props> = ({ projects, onOpenProject }) => {
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<ProjectImageFilter>('with-image');
    const [mediaByProject, setMediaByProject] = useState<Map<string, ProjectMedia[]>>(new Map());
    const [tabelleFehlt, setTabelleFehlt] = useState(false);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [zipProgress, setZipProgress] = useState<{ done: number; total: number } | null>(null);
    const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
    const [uploadTarget, setUploadTarget] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listAllProjectMedia();
            setMediaByProject(res.byProject);
            setTabelleFehlt(res.tabelleFehlt);
        } catch (e: any) {
            setMessage({ kind: 'error', text: e?.message || 'Bilder konnten nicht geladen werden.' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void reload(); }, [reload]);

    const rows = useMemo(() => buildProjectImageRows(projects, mediaByProject), [projects, mediaByProject]);
    const visible = useMemo(() => filterProjectImageRows(rows, search, filter), [rows, search, filter]);
    const withImageCount = useMemo(() => rows.filter(r => r.images.length > 0).length, [rows]);
    const totalImages = useMemo(() => rows.reduce((n, r) => n + r.images.length, 0), [rows]);
    const visibleEntries = useMemo<ZipEntry[]>(
        () => visible.flatMap(r => zipEntriesFor(r.images, r.projectNumber, r.name)),
        [visible],
    );

    const rawById = useMemo(() => {
        const m = new Map<string, ListProject>();
        (projects || []).forEach(p => m.set(p.id, p));
        return m;
    }, [projects]);

    const handleDownload = async (row: ProjectImageRow, image: ProjectMedia, index: number) => {
        setMessage(null);
        setBusyId(image.id);
        try {
            await downloadProjectReferenceImage(image, row.projectNumber, row.name, row.images.length > 1 ? index + 1 : undefined);
        } catch (e: any) {
            setMessage({ kind: 'error', text: e?.message || 'Download fehlgeschlagen.' });
        } finally {
            setBusyId(null);
        }
    };

    const runZip = async (entries: ZipEntry[]) => {
        if (entries.length === 0 || zipProgress) return;
        setMessage(null);
        setZipProgress({ done: 0, total: entries.length });
        try {
            const result = await downloadProjectReferenceImagesZip(entries, (done, total) => setZipProgress({ done, total }));
            if (result.failed.length > 0) {
                setMessage({ kind: 'error', text: `${result.ok} Bilder gepackt, ${result.failed.length} nicht erreichbar: ${result.failed.join(', ')}` });
            } else {
                setMessage({ kind: 'ok', text: `${result.ok} Bilder als ZIP heruntergeladen.` });
            }
        } catch (e: any) {
            setMessage({ kind: 'error', text: e?.message || 'ZIP fehlgeschlagen.' });
        } finally {
            setZipProgress(null);
        }
    };

    const handleCopy = async (row: ProjectImageRow) => {
        const text = [row.name, row.description, ...row.images.map(i => i.caption || '').filter(Boolean)].filter(Boolean).join('\n\n');
        try {
            await navigator.clipboard.writeText(text);
            setCopiedId(row.id);
            setTimeout(() => setCopiedId(c => (c === row.id ? null : c)), 1500);
        } catch {
            setMessage({ kind: 'error', text: 'Zwischenablage nicht verfügbar.' });
        }
    };

    const startUpload = (projectId: string) => {
        setUploadTarget(projectId);
        // Der Klick muss synchron zum Nutzer-Event laufen, sonst blockt der Browser den Dateidialog.
        fileInputRef.current?.click();
    };

    const handleFiles = async (files: FileList | null) => {
        const target = uploadTarget;
        const list = files ? Array.from(files) : [];
        if (!target || list.length === 0) return;
        setMessage(null);
        setBusyId(`upload:${target}`);
        let current = mediaByProject.get(target) || [];
        let ok = 0;
        const fehler: string[] = [];
        try {
            for (const f of list) {
                try {
                    const neu = await uploadProjectMedia(f, target, current);
                    current = [...current, neu];
                    ok++;
                } catch (e: any) {
                    fehler.push(`${f.name}: ${e?.message || 'Upload fehlgeschlagen'}`);
                }
            }
        } finally {
            setBusyId(null);
            setUploadTarget(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
        await reload();
        if (fehler.length > 0) setMessage({ kind: 'error', text: fehler.join(' · ') });
        else setMessage({ kind: 'ok', text: ok === 1 ? 'Bild hochgeladen.' : `${ok} Bilder hochgeladen.` });
    };

    return (
        <div className="space-y-6">
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={e => handleFiles(e.target.files)} />

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-100">
                    <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                        <Camera className="w-5 h-5 text-indigo-600" /> Projektbilder für Referenzen & Social Media
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-slate-500 font-medium">
                        <span>{withImageCount} von {rows.length} Projekten mit Bild · {totalImages} Bilder · {visible.length} angezeigt</span>
                        <button onClick={() => void reload()} disabled={loading} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-50" title="Neu laden">
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div className="space-y-1 md:col-span-1">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Suche</label>
                        <div className="relative">
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Projekt, Nr., Auftraggeber, Beschreibung, Bildunterschrift…"
                                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium"
                            />
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block">Anzeige</label>
                        <div className="flex gap-1.5 bg-slate-100 p-1 rounded-xl">
                            {([['with-image', 'Mit Bild'], ['without-image', 'Ohne Bild'], ['all', 'Alle']] as const).map(([val, label]) => (
                                <button
                                    key={val}
                                    onClick={() => setFilter(val)}
                                    className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${filter === val ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <button
                            onClick={() => runZip(visibleEntries)}
                            disabled={visibleEntries.length === 0 || zipProgress !== null}
                            className="px-4 py-2 text-sm font-bold rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
                        >
                            {zipProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                            {zipProgress ? `Packe ${zipProgress.done} / ${zipProgress.total} …` : `Alle angezeigten als ZIP (${visibleEntries.length})`}
                        </button>
                    </div>
                </div>

                {tabelleFehlt && (
                    <div className="text-xs rounded-lg px-3 py-2 border bg-amber-50 border-amber-200 text-amber-800 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{MEDIA_TABLE_MISSING_TEXT}</span>
                    </div>
                )}
                {message && (
                    <div className={`text-xs rounded-lg px-3 py-2 border flex items-start gap-2 ${message.kind === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
                        {message.kind === 'ok' ? <Check className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                        <span>{message.text}</span>
                    </div>
                )}
            </div>

            {visible.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400 font-medium space-y-2">
                    <ImageOff className="w-8 h-8 mx-auto text-slate-300" />
                    <div>{loading ? 'Lade Bilder …' : filter === 'with-image' ? 'Noch kein Projekt hat ein Bild.' : 'Keine Projekte entsprechen der Suche.'}</div>
                    {filter === 'with-image' && !loading && <div className="text-xs">Bilder lassen sich im Projekt unter „Projektbeschreibung & Projektbilder" oder hier über „Ohne Bild" → „Bilder hinzufügen" hochladen.</div>}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {visible.map(row => {
                        const titel = row.titelbild;
                        const verdict = titel ? resolutionVerdict(titel.width, titel.height) : 'unbekannt';
                        const raw = rawById.get(row.id);
                        const uploading = busyId === `upload:${row.id}`;
                        const others = row.images.filter(i => i.id !== titel?.id);
                        return (
                            <div key={row.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                                <div className="relative bg-slate-100 aspect-[4/3]">
                                    {titel ? (
                                        <a href={titel.url} target="_blank" rel="noreferrer" title="In voller Größe öffnen">
                                            <img src={titel.url} alt={row.name} className="w-full h-full object-cover" loading="lazy" />
                                        </a>
                                    ) : (
                                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-1">
                                            <ImageOff className="w-8 h-8" />
                                            <span className="text-xs font-medium">Kein Bild</span>
                                        </div>
                                    )}
                                    <div className="absolute top-2 left-2 flex gap-1.5">
                                        <span className="text-[10px] font-mono font-bold bg-white/90 text-slate-700 px-1.5 py-0.5 rounded shadow-sm flex items-center gap-1"><Hash className="w-3 h-3" />{row.projectNumber || 'N/A'}</span>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold shadow-sm ${row.isFinished ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                                            {row.isFinished ? 'Abgeschlossen' : 'Aktiv'}
                                        </span>
                                    </div>
                                    {titel && (
                                        <div className={`absolute bottom-2 right-2 text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm ${verdict === 'gut' ? 'bg-emerald-600 text-white' : verdict === 'knapp' ? 'bg-amber-500 text-white' : verdict === 'klein' ? 'bg-rose-600 text-white' : 'bg-slate-600 text-white'}`}>
                                            {titel.width && titel.height ? `${titel.width} × ${titel.height}` : 'Maße unbekannt'} · {describeFileSize(titel.sizeBytes)}
                                        </div>
                                    )}
                                    {row.images.length > 1 && (
                                        <div className="absolute bottom-2 left-2 text-[10px] font-bold bg-black/60 text-white px-1.5 py-0.5 rounded flex items-center gap-1"><Images className="w-3 h-3" /> {row.images.length}</div>
                                    )}
                                </div>

                                {others.length > 0 && (
                                    <div className="flex gap-1.5 px-3 pt-3 overflow-x-auto">
                                        {others.map(img => {
                                            const idx = row.images.findIndex(i => i.id === img.id);
                                            return (
                                                <button key={img.id} onClick={() => handleDownload(row, img, idx)} disabled={busyId !== null} className="relative flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border border-slate-200 hover:ring-2 hover:ring-indigo-400 disabled:opacity-60" title={`${img.fileName} herunterladen${img.caption ? ` — ${img.caption}` : ''}`}>
                                                    <img src={img.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                                                    {busyId === img.id && <span className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-indigo-600" /></span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="p-4 space-y-2 flex-1 flex flex-col">
                                    <h4 className="font-bold text-slate-800 text-sm leading-snug line-clamp-2">{row.name || 'Unbenanntes Projekt'}</h4>
                                    <div className="text-xs text-slate-500 space-y-1">
                                        <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /><span className="truncate">{row.customer || 'Kein Auftraggeber hinterlegt'}</span></div>
                                        <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" /><span>{row.period}</span></div>
                                    </div>
                                    {row.description ? (
                                        <p className="text-xs text-slate-600 leading-relaxed line-clamp-4 whitespace-pre-line" title={row.description}>{row.description}</p>
                                    ) : (
                                        <p className="text-xs text-slate-400 italic">Keine Projektbeschreibung.</p>
                                    )}

                                    <div className="flex flex-wrap gap-2 pt-2 mt-auto">
                                        {titel && (
                                            <button
                                                onClick={() => handleDownload(row, titel, 0)}
                                                disabled={busyId !== null}
                                                className="px-3 py-1.5 text-xs font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
                                                title="Titelbild in Originalauflösung"
                                            >
                                                {busyId === titel.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Titelbild
                                            </button>
                                        )}
                                        {row.images.length > 1 && (
                                            <button
                                                onClick={() => runZip(zipEntriesFor(row.images, row.projectNumber, row.name))}
                                                disabled={zipProgress !== null}
                                                className="px-3 py-1.5 text-xs font-bold rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 flex items-center gap-1.5"
                                            >
                                                <Archive className="w-3.5 h-3.5" /> Alle {row.images.length}
                                            </button>
                                        )}
                                        {(row.description || row.images.some(i => i.caption)) && (
                                            <button
                                                onClick={() => handleCopy(row)}
                                                className="px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
                                                title="Projektname, Beschreibung und Bildunterschriften in die Zwischenablage"
                                            >
                                                {copiedId === row.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />} Text
                                            </button>
                                        )}
                                        {!tabelleFehlt && (
                                            <button
                                                onClick={() => startUpload(row.id)}
                                                disabled={busyId !== null}
                                                className="px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5"
                                                title="Bilder zu diesem Projekt hochladen"
                                            >
                                                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />} {uploading ? 'Lädt …' : 'Hinzufügen'}
                                            </button>
                                        )}
                                        {onOpenProject && raw && (
                                            <button
                                                onClick={() => onOpenProject(raw)}
                                                className="px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-1.5 ml-auto"
                                                title="Projekt öffnen"
                                            >
                                                <ExternalLink className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
